import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Battery, 
  Zap, 
  MapPin, 
  Activity, 
  ArrowRight, 
  CheckCircle2, 
  Cpu, 
  ShieldCheck, 
  Layers, 
  Phone, 
  Mail, 
  Map, 
  Loader2,
  TrendingUp
} from 'lucide-react';
import Counter from '../components/Counter';

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeTab, setActiveTab] = useState('alle'); // 'alle', 'under-opfoerelse', 'i-pipeline'
  
  // Form state
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    location: '',
    message: '',
    website_url: '' // Honeypot field
  });

  // Fetch projects from CMS
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/v2/content/project');
        if (!res.ok) {
          throw new Error('Kunne ikke hente projekter fra databasen.');
        }
        const data = await res.json();
        
        const bornholmProject = {
          id: 'bornholm-pipeline',
          title: { da: 'Bornholm' },
          slug: { da: 'bornholm' },
          location: { da: 'Bornholm' },
          capacity: '4 projekter',
          power: 'I pipeline',
          description: { da: '<p>Vi har i øjeblikket 4 projekter i pipeline på Bornholm. Disse kommende BESS-anlæg vil bidrage væsentligt til den lokale forsyningssikkerhed og understøtte Bornholms ambition om at være et fuldstændigt grønt og CO2-neutralt samfund.</p>' },
          terms: {
            'project-status': ['i-pipeline']
          }
        };

        const allProjects = data.items ? [...data.items, bornholmProject] : [bornholmProject];
        setProjects(allProjects);
        
        // Set first project as selected by default
        if (allProjects.length > 0) {
          setSelectedProject(allProjects[0]);
        }
      } catch (err) {
        console.error('Error fetching projects:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Map project to its visual coordinates on our stylized SVG map
  const getMapCoords = (slug) => {
    const coords = {
      'loegumkloster': { x: 165, y: 390 },
      'toftlund': { x: 185, y: 370 },
      'hedensted': { x: 215, y: 285 },
      'vejle': { x: 205, y: 300 },
      'ringsted': { x: 415, y: 335 },
      'hobro': { x: 205, y: 195 },
      'odense': { x: 310, y: 350 },
      'bornholm': { x: 535, y: 310 },
      'bjerringbro-bess': { x: 220, y: 230 }
    };
    return coords[slug] || { x: 250, y: 250 };
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormError(null);

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setFormSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          location: '',
          message: '',
          website_url: ''
        });
      } else {
        throw new Error('Der skete en fejl ved afsendelsen. Prøv venligst igen.');
      }
    } catch (err) {
      setFormError(err.message);
    } finally {
      setFormSubmitting(false);
    }
  };

  // Filter projects based on active tab
  const filteredProjects = projects.filter(project => {
    const statusTerms = project.terms?.['project-status'] || [];
    if (activeTab === 'alle') return true;
    return statusTerms.includes(activeTab);
  });

  const projectsUnderConstruction = projects.filter(project => {
    const statusTerms = project.terms?.['project-status'] || [];
    return statusTerms.includes('under-opfoerelse');
  });

  const projectsInPipeline = projects.filter(project => {
    const statusTerms = project.terms?.['project-status'] || [];
    return statusTerms.includes('i-pipeline');
  });

  const projectsInOperation = projects.filter(project => {
    const statusTerms = project.terms?.['project-status'] || [];
    return statusTerms.includes('i-drift');
  });

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center bg-slate-950 text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=85,format=auto/assets/f4225015-9d61-46d9-9e4c-356d5fd3c807/chatgpt-image-22-jun-2026-13-34-34.png" 
            alt="GMA BESS Container" 
            className="w-full h-full object-cover opacity-35"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-transparent"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10 py-20">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-[#62b2c9]/20 text-[#62b2c9] text-xs font-black uppercase tracking-widest mb-6 border border-[#62b2c9]/30">
              <Activity className="w-3.5 h-3.5 animate-pulse" /> BESS Projekter - Energilagring til fremtidens energisystem
            </span>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight uppercase tracking-tighter">
              827 MWh / 265 MW <br />
              <span className="text-[#62b2c9]">Pipeline i Danmark</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-2xl font-medium mb-8">
              GMA Solar udvikler og etablerer batteribaserede energilagringsanlæg (BESS) over hele Danmark. Vores projekter bidrager til et mere fleksibelt, stabilt og grønt energisystem ved at understøtte integrationen af vedvarende energi og styrke elnettets driftssikkerhed.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pipeline in Numbers */}
      <section className="relative z-20 -mt-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { value: 827, suffix: " MWh", label: "Samlet energikapacitet", desc: "Batterilagringskapacitet i pipeline" },
              { value: 265, suffix: " MW", label: "Samlet effekt", desc: "Maksimal nettilslutningseffekt" },
              { value: 2, suffix: "", label: "Under opførelse", desc: "Aktive anlæg under etablering" },
              { value: "Danmark", isText: true, label: "Landsdækkende", desc: "Projektudvikling i hele landet" }
            ].map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-white p-8 shadow-xl border-b-4 border-[#62b2c9] flex flex-col justify-between"
              >
                <div>
                  <div className="text-4xl md:text-5xl font-black text-slate-900 mb-2 tracking-tight">
                    {stat.isText ? (
                      stat.value
                    ) : (
                      <Counter value={stat.value} suffix={stat.suffix} />
                    )}
                  </div>
                  <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">{stat.label}</h3>
                </div>
                <p className="text-xs text-slate-500 font-medium">{stat.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Map Section */}
      <section id="kort" className="py-24 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-xs font-black text-[#62b2c9] uppercase tracking-[0.3em] mb-3">Landsdækkende udvikling</h2>
            <h3 className="text-3xl md:text-5xl font-black text-slate-900 uppercase tracking-tighter mb-6">
              Interaktivt Projektkort
            </h3>
            <p className="text-slate-600 font-medium">
              Klik på punkterne på kortet for at se detaljer om vores BESS-projekter under opførelse og i pipeline. Vi dækker hele Danmark med intelligent energilagring.
            </p>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="w-12 h-12 text-[#62b2c9] animate-spin mb-4" />
              <p className="text-slate-500 font-bold uppercase tracking-wider text-xs">Henter projekter...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 text-red-600 p-6 text-center rounded-none border border-red-200 max-w-xl mx-auto">
              <p className="font-bold mb-2">Der opstod en fejl:</p>
              <p className="text-sm">{error}</p>
            </div>
          ) : (
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              {/* SVG Map of Denmark */}
              <div className="lg:col-span-7 bg-white p-6 md:p-10 shadow-lg border border-slate-100 flex justify-center relative overflow-hidden">
                <div className="absolute top-4 left-4 flex flex-col gap-2 z-10 bg-white/90 backdrop-blur-sm p-3 border border-slate-100 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-emerald-500 inline-block"></span>
                    <span className="font-bold text-slate-700">I drift</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-[#62b2c9] inline-block animate-pulse"></span>
                    <span className="font-bold text-slate-700">Under opførelse</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-slate-400 inline-block"></span>
                    <span className="font-bold text-slate-700">I pipeline</span>
                  </div>
                </div>

                <svg 
                  viewBox="0 0 600 500" 
                  className="w-full max-w-[550px] h-auto drop-shadow-md"
                >
                  {/* Stylized Denmark Map Paths */}
                  <g className="fill-slate-100 stroke-slate-200 stroke-2">
                    {/* Jutland (Jylland) */}
                    <path d="M 210,30 L 220,45 L 225,60 L 220,80 L 225,95 L 215,105 L 215,125 L 225,135 L 220,150 L 245,160 L 255,175 L 245,195 L 230,200 L 225,215 L 230,235 L 220,255 L 225,275 L 215,290 L 210,310 L 195,325 L 205,345 L 195,365 L 198,385 L 185,400 L 165,410 L 155,410 L 150,380 L 145,350 L 140,320 L 135,280 L 130,240 L 125,200 L 122,170 L 140,155 L 145,135 L 140,110 L 155,90 L 175,70 L 195,50 Z" className="hover:fill-slate-200/70 transition-colors duration-300" />
                    {/* Funen (Fyn) */}
                    <path d="M 280,325 L 295,315 L 305,310 L 315,315 L 325,330 L 330,345 L 320,365 L 315,385 L 305,375 L 290,370 L 275,355 L 270,340 Z" className="hover:fill-slate-200/70 transition-colors duration-300" />
                    {/* Zealand (Sjælland) */}
                    <path d="M 350,245 L 365,255 L 380,250 L 395,240 L 415,225 L 428,225 L 435,235 L 440,255 L 445,275 L 440,290 L 435,305 L 430,325 L 415,340 L 418,355 L 405,365 L 395,360 L 385,365 L 370,360 L 365,345 L 355,335 L 350,315 L 355,295 L 365,280 L 355,265 Z" className="hover:fill-slate-200/70 transition-colors duration-300" />
                    {/* Lolland */}
                    <path d="M 345,385 L 380,380 L 385,390 L 375,400 L 345,395 Z" className="hover:fill-slate-200/70 transition-colors duration-300" />
                    {/* Falster */}
                    <path d="M 385,390 L 400,390 L 398,410 L 385,400 Z" className="hover:fill-slate-200/70 transition-colors duration-300" />
                    {/* Møn */}
                    <path d="M 418,355 L 430,360 L 425,370 L 415,365 Z" className="hover:fill-slate-200/70 transition-colors duration-300" />
                    {/* Bornholm */}
                    <path d="M 525,295 L 555,305 L 545,325 L 515,315 Z" className="hover:fill-slate-200/70 transition-colors duration-300" />
                  </g>

                  {/* Connection lines for selected project */}
                  {selectedProject && (
                    <g>
                      {(() => {
                        const coords = getMapCoords(selectedProject.slug?.da || selectedProject.slug);
                        return (
                          <line 
                            x1={coords.x} 
                            y1={coords.y} 
                            x2={coords.x} 
                            y2={coords.y - 30} 
                            stroke="#62b2c9" 
                            strokeWidth="1.5" 
                            strokeDasharray="4,4"
                          />
                        );
                      })()}
                    </g>
                  )}

                  {/* Project Points */}
                  {projects.map((project) => {
                    const slug = project.slug?.da || project.slug;
                    const coords = getMapCoords(slug);
                    const isSelected = selectedProject?.id === project.id;
                    const isUnderConstruction = project.terms?.['project-status']?.includes('under-opfoerelse');
                    const isOperating = project.terms?.['project-status']?.includes('i-drift');

                    return (
                      <g 
                        key={project.id}
                        className="cursor-pointer"
                        onClick={() => setSelectedProject(project)}
                      >
                        {/* Pulsing Outer Ring */}
                        {isUnderConstruction && (
                          <circle 
                            cx={coords.x} 
                            cy={coords.y} 
                            r={isSelected ? 16 : 10} 
                            className="fill-[#62b2c9]/20 stroke-[#62b2c9]/40 stroke-1 animate-ping"
                            style={{ transformOrigin: `${coords.x}px ${coords.y}px` }}
                          />
                        )}

                        {/* Solid Dot */}
                        <circle 
                          cx={coords.x} 
                          cy={coords.y} 
                          r={isSelected ? 8 : 6} 
                          className={`transition-all duration-300 ${
                            isOperating
                              ? isSelected ? 'fill-emerald-500 stroke-white stroke-2' : 'fill-emerald-500 hover:fill-emerald-400'
                              : isUnderConstruction 
                                ? isSelected ? 'fill-[#62b2c9] stroke-white stroke-2' : 'fill-[#62b2c9] hover:fill-[#62b2c9]/80'
                                : isSelected ? 'fill-slate-800 stroke-white stroke-2' : 'fill-slate-400 hover:fill-slate-500'
                          }`}
                        />

                        {/* Label on hover or selection */}
                        {isSelected && (
                          <g>
                            <rect 
                              x={coords.x - 60} 
                              y={coords.y - 55} 
                              width="120" 
                              height="22" 
                              rx="3" 
                              className="fill-slate-900 shadow-lg"
                            />
                            <text 
                              x={coords.x} 
                              y={coords.y - 40} 
                              textAnchor="middle" 
                              className="fill-white text-[10px] font-black uppercase tracking-wider"
                            >
                              {project.title?.da || project.title}
                            </text>
                          </g>
                        )}
                      </g>
                    );
                  })}
                </svg>
              </div>

              {/* Selected Project Details Card */}
              <div className="lg:col-span-5">
                <AnimatePresence mode="wait">
                  {selectedProject && (
                    <motion.div
                      key={selectedProject.id}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                      className="bg-white p-8 md:p-10 shadow-xl border border-slate-100 flex flex-col justify-between min-h-[450px]"
                    >
                      <div>
                        <div className="flex items-center justify-between mb-6">
                          <span className={`px-3 py-1 text-[10px] font-black uppercase tracking-widest border ${
                            selectedProject.terms?.['project-status']?.includes('i-drift')
                              ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'
                              : selectedProject.terms?.['project-status']?.includes('under-opfoerelse')
                                ? 'bg-[#62b2c9]/10 text-[#62b2c9] border-[#62b2c9]/20'
                                : 'bg-slate-100 text-slate-600 border-slate-200'
                          }`}>
                            {selectedProject.terms?.['project-status']?.includes('i-drift') 
                              ? 'I drift' 
                              : selectedProject.terms?.['project-status']?.includes('under-opfoerelse') 
                                ? 'Under opførelse' 
                                : 'I pipeline'}
                          </span>
                          <span className="text-xs font-bold text-slate-400 flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" /> {selectedProject.location?.da || selectedProject.location}
                          </span>
                        </div>

                        <h4 className="text-3xl font-black text-slate-900 uppercase tracking-tighter mb-2">
                          {selectedProject.title?.da || selectedProject.title}
                        </h4>
                        <p className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-8">BESS Energilager</p>

                        {/* Specifications Grid */}
                        <div className="grid grid-cols-2 gap-6 mb-8 bg-slate-50 p-6 border border-slate-100">
                          <div>
                            <div className="text-xs font-black text-slate-400 uppercase tracking-wider mb-1">Kapacitet</div>
                            <div className="text-2xl font-black text-slate-900">{selectedProject.capacity}</div>
                          </div>
                          <div>
                            <div className="text-xs font-black text-slate-400 uppercase tracking-wider mb-1">Effekt</div>
                            <div className="text-2xl font-black text-slate-900">{selectedProject.power}</div>
                          </div>
                        </div>

                        {/* Description */}
                        <div 
                          className="text-slate-600 leading-relaxed mb-8 text-sm font-medium prose prose-slate"
                          dangerouslySetInnerHTML={{ __html: selectedProject.description?.da || selectedProject.description }}
                        />
                      </div>

                      <div>
                        {selectedProject.terms?.['project-status']?.includes('i-drift') ? (
                          <Link 
                            to={`/projekter/${selectedProject.slug?.da || selectedProject.slug}`}
                            className="inline-flex items-center gap-2 text-xs font-black text-emerald-600 uppercase tracking-widest hover:text-emerald-700 transition-colors"
                          >
                            Læs hele casen <ArrowRight className="w-4 h-4" />
                          </Link>
                        ) : selectedProject.terms?.['project-status']?.includes('under-opfoerelse') ? (
                          <a 
                            href="#projekter-liste"
                            className="inline-flex items-center gap-2 text-xs font-black text-[#62b2c9] uppercase tracking-widest hover:text-slate-950 transition-colors"
                          >
                            Se fulde specifikationer <ArrowRight className="w-4 h-4" />
                          </a>
                        ) : (
                          <p className="text-xs text-slate-400 italic font-medium">
                            Dette projekt er i planlægningsfasen og afventer endelige godkendelser.
                          </p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Projects In Operation (Cases) Section */}
      {projectsInOperation.length > 0 && (
        <section id="referencer" className="py-24 bg-slate-50 border-t border-slate-100">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mb-16">
              <h2 className="text-xs font-black text-emerald-600 uppercase tracking-[0.3em] mb-3">Referencer</h2>
              <h3 className="text-3xl md:text-5xl font-black text-slate-900 uppercase tracking-tighter">
                Anlæg i drift
              </h3>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {projectsInOperation.map((project) => {
                const mediaUrl = project.video?.[0] || project.image?.[0] || "https://assets.auxodev.dk/assets/f4225015-9d61-46d9-9e4c-356d5fd3c807/ba451ae2-b04c-4ed3-85ce-a8edf6177646.jpg";
                const isVideo = mediaUrl.match(/\.(mp4|webm|ogg)$/i);
                
                const resizedImageUrl = (!isVideo && mediaUrl.includes('assets.auxodev.dk'))
                  ? mediaUrl.replace('assets.auxodev.dk/assets/', 'assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/')
                  : mediaUrl;

                return (
                  <motion.div 
                    key={project.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="bg-white border border-slate-100 overflow-hidden flex flex-col shadow-lg group"
                  >
                    <Link to={`/projekter/${project.slug?.da || project.slug}`} className="block relative aspect-[16/10] overflow-hidden bg-slate-900">
                      {isVideo ? (
                        <video 
                          src={mediaUrl} 
                          autoPlay 
                          loop 
                          muted 
                          playsInline
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-80"
                        />
                      ) : (
                        <img 
                          src={resizedImageUrl} 
                          alt={project.title?.da || project.title} 
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      )}
                      <div className="absolute top-4 left-4 bg-emerald-500 text-white px-3 py-1 text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5">
                        <CheckCircle2 className="w-3 h-3" /> I drift
                      </div>
                      <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/20 transition-colors duration-300 flex items-center justify-center">
                        <span className="opacity-0 group-hover:opacity-100 bg-white text-slate-900 px-4 py-2 text-xs font-black uppercase tracking-widest transform translate-y-4 group-hover:translate-y-0 transition-all duration-300">
                          Læs case
                        </span>
                      </div>
                    </Link>
                    
                    <div className="p-8 flex-grow flex flex-col justify-between">
                      <div>
                        <div className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" /> {project.location?.da || project.location}
                        </div>
                        <h4 className="text-2xl font-black text-slate-900 uppercase tracking-tighter mb-4">
                          <Link to={`/projekter/${project.slug?.da || project.slug}`} className="hover:text-[#62b2c9] transition-colors">
                            {project.title?.da || project.title}
                          </Link>
                        </h4>
                      </div>

                      <div className="grid grid-cols-2 gap-4 border-t border-slate-100 pt-6 mt-6">
                        <div>
                          <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Kapacitet</div>
                          <div className="text-lg font-black text-slate-900">{project.capacity}</div>
                        </div>
                        <div>
                          <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Effekt</div>
                          <div className="text-lg font-black text-slate-900">{project.power}</div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Projects Under Construction Section */}
      <section id="projekter-liste" className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mb-16">
            <h2 className="text-xs font-black text-[#62b2c9] uppercase tracking-[0.3em] mb-3">Aktuelle anlæg</h2>
            <h3 className="text-3xl md:text-5xl font-black text-slate-900 uppercase tracking-tighter">
              Projekter under opførelse
            </h3>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#62b2c9] animate-spin" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-12">
              {projectsUnderConstruction.map((project) => {
                const imageUrl = project.image?.[0] || "https://assets.auxodev.dk/assets/f4225015-9d61-46d9-9e4c-356d5fd3c807/ba451ae2-b04c-4ed3-85ce-a8edf6177646.jpg";
                // Apply Cloudflare resizing for performance
                const resizedImageUrl = imageUrl.includes('assets.auxodev.dk') 
                  ? imageUrl.replace('assets.auxodev.dk/assets/', 'assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/')
                  : imageUrl;

                return (
                  <motion.div 
                    key={project.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="bg-slate-50 border border-slate-100 overflow-hidden flex flex-col"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden bg-slate-900">
                      <img 
                        src={resizedImageUrl} 
                        alt={project.title?.da || project.title} 
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-4 left-4 bg-[#62b2c9] text-white px-3 py-1 text-[10px] font-black uppercase tracking-widest">
                        Under opførelse
                      </div>
                    </div>
                    
                    <div className="p-8 md:p-10 flex-grow flex flex-col justify-between">
                      <div>
                        <div className="text-xs font-black text-[#62b2c9] uppercase tracking-widest mb-2 flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" /> {project.title?.da || project.title}, {project.location?.da || project.location}
                        </div>
                        <h4 className="text-2xl md:text-3xl font-black text-slate-900 uppercase tracking-tighter mb-4">
                          {project.title?.da || project.title} BESS
                        </h4>
                        <div 
                          className="text-slate-600 text-sm leading-relaxed mb-8 font-medium"
                          dangerouslySetInnerHTML={{ __html: project.description?.da || project.description }}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4 border-t border-slate-200 pt-6">
                        <div>
                          <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Kapacitet</div>
                          <div className="text-xl font-black text-slate-900">{project.capacity}</div>
                        </div>
                        <div>
                          <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Effekt</div>
                          <div className="text-xl font-black text-slate-900">{project.power}</div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Technology Section */}
      <section className="py-24 bg-slate-950 text-white overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-12 gap-16 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-5"
            >
              <span className="text-xs font-black text-[#62b2c9] uppercase tracking-[0.3em] mb-4 block">Teknologi</span>
              <h3 className="text-3xl md:text-5xl font-black uppercase tracking-tighter mb-8 leading-tight">
                Standardiseret BESS-løsning fra GMA Solar
              </h3>
              <p className="text-slate-300 leading-relaxed mb-8 font-medium">
                Vores energilagringsanlæg er baseret på modulære batterisystemer med høj driftssikkerhed, avanceret overvågning og intelligent energistyring. Løsningen kan skaleres fra mindre lokale anlæg til større utility-scale energilagre.
              </p>

              <div className="space-y-6">
                {[
                  { icon: Cpu, title: "Intelligent styring", desc: "Avanceret EMS (Energy Management System) sikrer optimal drift og netydelser." },
                  { icon: ShieldCheck, title: "Høj sikkerhed", desc: "Multi-level sikkerhedssystemer med integreret brandslukning og overvågning." },
                  { icon: Layers, title: "Modulært design", desc: "Nem skalering og hurtig installation med standardiserede container-løsninger." }
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#62b2c9]/10 border border-[#62b2c9]/20 flex items-center justify-center text-[#62b2c9]">
                      <item.icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="font-black uppercase tracking-wider text-sm mb-1 text-white">{item.title}</h4>
                      <p className="text-xs text-slate-400 leading-relaxed font-medium">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-7 relative"
            >
              <div className="absolute inset-0 bg-[#62b2c9]/10 blur-3xl rounded-full pointer-events-none"></div>
              <img 
                src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/f4225015-9d61-46d9-9e4c-356d5fd3c807/star-h-max-billede.png" 
                alt="GMA Solar BESS Enhed" 
                className="w-full h-auto object-cover border border-white/10 shadow-2xl relative z-10"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Selected Projects in Pipeline (Table/List) */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-xs font-black text-[#62b2c9] uppercase tracking-[0.3em] mb-3">Fremtidig kapacitet</h2>
            <h3 className="text-3xl md:text-5xl font-black text-slate-900 uppercase tracking-tighter mb-6">
              Udvalgte projekter i pipeline
            </h3>
            <p className="text-slate-600 font-medium">
              GMA Solar har en omfattende portefølje af energilagringsprojekter i udvikling over hele Danmark. Her er et udvalg af kommende anlæg.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#62b2c9] animate-spin" />
            </div>
          ) : (
            <div className="max-w-4xl mx-auto">
              <div className="bg-slate-50 border border-slate-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-slate-200 text-[10px] font-black text-slate-400 uppercase tracking-widest bg-slate-100/50">
                        <th className="py-5 px-6">Lokation</th>
                        <th className="py-5 px-6">Region</th>
                        <th className="py-5 px-6">Kapacitet</th>
                        <th className="py-5 px-6">Effekt</th>
                        <th className="py-5 px-6 text-right">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-sm font-bold text-slate-700">
                      {projectsInPipeline.map((project) => (
                        <tr key={project.id} className="hover:bg-slate-100/30 transition-colors">
                          <td className="py-5 px-6 text-slate-900 font-black uppercase tracking-tight">
                            {project.title?.da || project.title}
                          </td>
                          <td className="py-5 px-6 text-slate-500">
                            {project.location?.da || project.location}
                          </td>
                          <td className="py-5 px-6 text-slate-900 font-black">
                            {project.capacity}
                          </td>
                          <td className="py-5 px-6 text-slate-500">
                            {project.power}
                          </td>
                          <td className="py-5 px-6 text-right">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider bg-slate-100 text-slate-500 border border-slate-200">
                              I pipeline
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-8 p-6 bg-slate-50 border border-slate-100 text-center">
                <p className="text-xs text-slate-500 font-medium leading-relaxed">
                  Flere projekter er i sen udviklingsfase og afventer de sidste myndigheds- og netgodkendelser. Med en samlet pipeline på <strong className="text-slate-900">827 MWh / 265 MW</strong> er vi blandt de aktører, der bidrager væsentligt til udbygningen af energilagring og fleksibilitet i det danske energisystem. <span className="text-[#62b2c9] font-bold">Derudover har vi 4 spændende projekter i pipeline på Bornholm.</span>
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-slate-50 border-t border-b border-slate-100">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-5">
              <span className="text-xs font-black text-[#62b2c9] uppercase tracking-[0.3em] mb-4 block">Landsdækkende udvikling</span>
              <h3 className="text-3xl md:text-5xl font-black text-slate-900 uppercase tracking-tighter mb-6 leading-tight">
                Landsdækkende udvikling
              </h3>
              <p className="text-slate-600 leading-relaxed font-medium">
                GMA Solar arbejder aktivt med udvikling af energilagringsprojekter på tværs af Danmark – herunder har vi 4 projekter i pipeline på Bornholm. Med en samlet pipeline på <strong className="text-slate-900">827 MWh / 265 MW</strong> er vi blandt de aktører, der bidrager væsentligt til udbygningen af energilagring og fleksibilitet i det danske energisystem.
              </p>
            </div>
            <div className="lg:col-span-7">
              <h4 className="text-sm font-black text-slate-900 uppercase tracking-wider mb-6">Vores projekter understøtter:</h4>
              <div className="grid sm:grid-cols-2 gap-6">
                {[
                  { title: "Netstabilisering", desc: "Sikrer elnettets driftssikkerhed og leverer lynhurtige netydelser til at stabilisere frekvensen." },
                  { title: "Integration af vedvarende energi", desc: "Gør det muligt at optage mere strøm fra solceller og vindmøller i det samlede system." },
                  { title: "Balancering af elnettet", desc: "Udligner forskelle mellem forbrug og produktion i realtid og styrker balancen." },
                  { title: "Lokal energifleksibilitet", desc: "Aflaster lokale transformatorstationer og reducerer behovet for dyre netudbygninger." },
                  { title: "Den grønne omstilling", desc: "Understøtter udfasningen af fossile brændstoffer og skaber fremtidens energisystem." }
                ].map((benefit, idx) => (
                  <div key={idx} className={`bg-white p-8 shadow-md border-l-4 border-[#62b2c9] ${idx === 4 ? "sm:col-span-2" : ""}`}>
                    <h5 className="font-black uppercase tracking-wider text-sm mb-2 text-slate-900">{benefit.title}</h5>
                    <p className="text-xs text-slate-500 leading-relaxed font-medium">{benefit.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action & Contact Form */}
      <section id="kontakt" className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-12 gap-16 items-start">
            {/* Left Column Info */}
            <div className="lg:col-span-5 lg:sticky lg:top-28">
              <span className="text-xs font-black text-[#62b2c9] uppercase tracking-[0.3em] mb-4 block">Samarbejde</span>
              <h3 className="text-3xl md:text-5xl font-black text-slate-900 uppercase tracking-tighter mb-6 leading-tight">
                Har du et projekt eller en lokation?
              </h3>
              <p className="text-slate-600 leading-relaxed mb-10 font-medium">
                GMA Solar samarbejder med lodsejere, investorer, virksomheder og netoperatører om udvikling af fremtidens energilagringsanlæg.
                <span className="block mt-4 font-bold text-slate-900">Kontakt os for en uforpligtende dialog om mulighederne.</span>
              </p>

              <div className="space-y-6 border-t border-slate-100 pt-8">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-slate-50 flex items-center justify-center text-slate-900 border border-slate-100">
                    <Phone className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Ring til os</div>
                    <a href="tel:+4551811000" className="text-sm font-bold text-slate-900 hover:text-[#62b2c9] transition-colors">+45 51 81 10 00</a>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-slate-50 flex items-center justify-center text-slate-900 border border-slate-100">
                    <Mail className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Skriv en e-mail</div>
                    <a href="mailto:info@gmasolar.dk" className="text-sm font-bold text-slate-900 hover:text-[#62b2c9] transition-colors">info@gmasolar.dk</a>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column Form */}
            <div className="lg:col-span-7 bg-slate-50 p-8 md:p-12 border border-slate-100 shadow-xl">
              <h4 className="text-2xl font-black text-slate-900 uppercase tracking-tighter mb-8">Send os en besked</h4>
              
              {formSubmitted ? (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-8 text-center"
                >
                  <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                  <h5 className="text-lg font-black uppercase tracking-wider mb-2">Tak for din henvendelse!</h5>
                  <p className="text-sm font-medium text-emerald-700">
                    Vi har modtaget din besked og vender tilbage til dig hurtigst muligt.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleFormSubmit} className="space-y-6">
                  {/* Honeypot field - absolutely hidden from humans */}
                  <div className="sr-only absolute overflow-hidden w-0 h-0" aria-hidden="true">
                    <label htmlFor="website_url">Do not fill this field</label>
                    <input 
                      type="text" 
                      id="website_url" 
                      name="website_url" 
                      value={formData.website_url} 
                      onChange={handleFormChange} 
                      tabIndex="-1" 
                      autoComplete="off" 
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2">Navn *</label>
                      <input 
                        type="text" 
                        name="name" 
                        required 
                        value={formData.name}
                        onChange={handleFormChange}
                        className="w-full bg-white border border-slate-200 px-4 py-3 text-sm font-bold text-slate-900 focus:outline-none focus:border-[#62b2c9] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2">E-mail *</label>
                      <input 
                        type="email" 
                        name="email" 
                        required 
                        value={formData.email}
                        onChange={handleFormChange}
                        className="w-full bg-white border border-slate-200 px-4 py-3 text-sm font-bold text-slate-900 focus:outline-none focus:border-[#62b2c9] transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2">Telefonnummer</label>
                      <input 
                        type="tel" 
                        name="phone" 
                        value={formData.phone}
                        onChange={handleFormChange}
                        className="w-full bg-white border border-slate-200 px-4 py-3 text-sm font-bold text-slate-900 focus:outline-none focus:border-[#62b2c9] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2">Virksomhed</label>
                      <input 
                        type="text" 
                        name="company" 
                        value={formData.company}
                        onChange={handleFormChange}
                        className="w-full bg-white border border-slate-200 px-4 py-3 text-sm font-bold text-slate-900 focus:outline-none focus:border-[#62b2c9] transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2">Lokation / Projektadresse</label>
                    <input 
                      type="text" 
                      name="location" 
                      placeholder="F.eks. postnummer eller by"
                      value={formData.location}
                      onChange={handleFormChange}
                      className="w-full bg-white border border-slate-200 px-4 py-3 text-sm font-bold text-slate-900 focus:outline-none focus:border-[#62b2c9] transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2">Besked / Projektbeskrivelse *</label>
                    <textarea 
                      name="message" 
                      rows="5" 
                      required 
                      value={formData.message}
                      onChange={handleFormChange}
                      className="w-full bg-white border border-slate-200 p-4 text-sm font-bold text-slate-900 focus:outline-none focus:border-[#62b2c9] transition-colors resize-none"
                    ></textarea>
                  </div>

                  {formError && (
                    <div className="text-xs font-bold text-red-600 bg-red-50 p-4 border border-red-100">
                      {formError}
                    </div>
                  )}

                  <button 
                    type="submit" 
                    disabled={formSubmitting}
                    className="w-full bg-slate-900 hover:bg-black text-white py-4 font-black uppercase tracking-widest text-xs transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {formSubmitting ? (
                      <>
                        Sender besked... <Loader2 className="w-4 h-4 animate-spin" />
                      </>
                    ) : (
                      <>
                        Send besked <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectsPage;
